import { Fragment } from "react";

import { Accordion } from "common/components/ui/Accordion";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { AUDIENCE_FACET_KEYS } from "../../../../constants";
import AudienceFilterContent from "./AudienceFilterContent";
import AudienceFilterDesktop from "./AudienceFilterDesktop";
import AudienceFilterMobile from "./AudienceFilterMobile";

import type {
  AudienceFacetItem,
  AudienceFacets,
  AudienceFilters as AudienceFiltersType,
} from "../../../../types";

interface AudienceFiltersProps {
  audienceFacets: AudienceFacets;
  isLoadingFacets: boolean;
  isMiningTargets: boolean;
  filters: AudienceFiltersType;
  setFilters: (filters: AudienceFiltersType) => void;
}
export default function AudienceFilters({
  audienceFacets,
  isLoadingFacets,
  isMiningTargets,
  filters,
  setFilters,
}: AudienceFiltersProps) {
  const isDesktop = useTwBreakpoint("lg");

  const content = AUDIENCE_FACET_KEYS.map((facetKey) => {
    const FilterComponent = isDesktop
      ? AudienceFilterDesktop
      : AudienceFilterMobile;

    let facetOptions: AudienceFacetItem[] = [];

    if (audienceFacets) {
      facetOptions =
        facetKey === "premium"
          ? [
              { id: undefined, name: "All", target_count: 0 },
              // this should be done on the server, facet names should be corrected
              ...audienceFacets[facetKey].map(({ name, ...rest }) => ({
                name: name === "true" ? "Premium" : "Not premium",
                ...rest,
              })),
            ]
          : audienceFacets[facetKey];
    }
    // this should be done on the server, invalid values shouldn't be returned by the api
    facetOptions = facetOptions.filter(({ id }) => id !== null);

    const filterComponent = (
      <FilterComponent
        key={facetKey}
        facetKey={facetKey}
        isMiningTargets={isMiningTargets}
        filters={filters}
        contentComponent={
          <AudienceFilterContent
            facetKey={facetKey}
            options={facetOptions}
            filters={filters}
            setFilters={setFilters}
            isLoadingFacets={isLoadingFacets}
          />
        }
      />
    );

    return isMiningTargets ? (
      <Tooltip key={facetKey}>
        <TooltipTrigger asChild>
          <div>{filterComponent}</div>
        </TooltipTrigger>

        <TooltipContent>
          You will be able to filter and export your Audience after all targets
          are loaded
        </TooltipContent>
      </Tooltip>
    ) : (
      <Fragment key={facetKey}>{filterComponent}</Fragment>
    );
  });
  if (isDesktop) {
    return (
      <div className="flex max-w-full flex-row flex-wrap gap-2">{content}</div>
    );
  }
  return (
    <Accordion type="multiple" className="my-4 w-full">
      {content}
    </Accordion>
  );
}
